/**
 * The page-level states of the checkout, in one place, so the orchestrator
 * below reads as a switch over what the service said rather than as layout.
 *
 * The mapping is the one the invoicing pages already use, plus the two states
 * that only this page has:
 *
 * | situation                                   | state                     |
 * |---------------------------------------------|---------------------------|
 * | the read is in flight                       | `LoadingTable`            |
 * | the service answered, with nothing to show  | `EmptyState`              |
 * | the service refused                         | `NotAvailableState` + `SupportDetails` |
 * | the service refused with a 403              | `PermissionDeniedState`   |
 * | unknown / revoked / malformed link           | the not-active state      |
 * | the invoice is closed                        | the closed state          |
 *
 * Nothing here renders a value it was not given. In particular the not-active,
 * closed and rate-limited states take **no** reference and no code: a page that
 * showed a support reference for a link that does not exist would be telling a
 * scanner which of its guesses landed.
 */

import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { LoadingTable, NotAvailableState, PermissionDeniedState } from "@/components/ui/system-state";
import { PAY_COPY } from "./copy";
import type { PayError } from "./pay-api";

/** Wraps a state card with the retry affordance and the reference line under it. */
function WithActions({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3">
      {children}
      {actions}
    </div>
  );
}

/**
 * Loading is a skeleton, never a spinner over stale content: the page has
 * nothing to show until the service answers, and a layout that filled in
 * approximations first would be the same defect as sample data, one re-render
 * earlier.
 */
export function CheckoutLoading() {
  return <LoadingTable rows={4} cols={2} label={PAY_COPY.loadingLabel} />;
}

export function CheckoutNotActive() {
  return (
    <NotAvailableState
      title={PAY_COPY.notActiveTitle}
      description={PAY_COPY.notActiveDescription}
    />
  );
}

export function CheckoutClosed() {
  return <NotAvailableState title={PAY_COPY.closedTitle} description={PAY_COPY.notActiveDescription} />;
}

export function CheckoutRateLimited({ onRetry }: { onRetry: () => void }) {
  return (
    <WithActions
      actions={
        <Button variant="secondary" onClick={onRetry}>
          {PAY_COPY.rateLimitedRetry}
        </Button>
      }
    >
      <NotAvailableState title={PAY_COPY.rateLimitedTitle} description={PAY_COPY.rateLimitedDescription} />
    </WithActions>
  );
}

export function CheckoutDenied({ error }: { error: PayError }) {
  return (
    <PermissionDeniedState
      title={PAY_COPY.deniedTitle}
      description={PAY_COPY.deniedDescription}
      details={[{ k: "Reason", v: error.reasonCode }]}
      footnote={<SupportDetails reference={error.supportReference} capability={error.capability} />}
    />
  );
}

/**
 * A refusal that is not a denial and not a dead link: a 5xx, a transport
 * failure, or a body the page could not read. The service's own code is shown
 * verbatim (bounded by the transport) so support can act on it, and the payer
 * is told plainly that nothing was charged.
 */
export function CheckoutError({
  error,
  status,
  onRetry,
}: {
  error: PayError;
  status: number;
  onRetry: () => void;
}) {
  const offline = status === 0;
  return (
    <WithActions
      actions={
        <Button variant="secondary" onClick={onRetry}>
          {PAY_COPY.errorRetry}
        </Button>
      }
    >
      <NotAvailableState
        title={offline ? PAY_COPY.offlineTitle : PAY_COPY.errorTitle}
        description={offline ? PAY_COPY.offlineDescription : PAY_COPY.errorDescription}
        reason={`${PAY_COPY.footerSupport}: ${error.reasonCode}`}
      />
      <SupportDetails reference={error.supportReference} capability={error.capability} />
    </WithActions>
  );
}
